/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-underscore-dangle */
import _ from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import api from '~/services';
import {
  log,
  getIntegrations,
  validateNumberType,
  getDetailsFromTo,
} from '~/helpers';
import { DEFAULT_USER_TIMEZONE } from '~/config/config';

import { filterOptions } from '~/store/reducers/historyFilterOptions';
import * as actionTypes from '~/store/actions/actionTypes';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Parses a date-ish value into a valid Date, or undefined when missing/unparseable.
 */
export const parseDate = value => {
  if (!value) {
    return undefined;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

// Format logs group name based on date
const formatGroupName = logItem =>
  dayjs(logItem.callDate).format('DD MMMM YYYY');

const convertSnakeLogsToCamelCase = calllogsResp => {
  return calllogsResp.map(call => ({
    id: call.call_id,
    /**
     * International dialing prefix format e.g: 003538300000000
     */
    internationalTo: call.to?.replace('+', '00'),
    /**
     * International dialing prefix format e.g: 003538300000000
     */
    internationalFrom: call.from?.replace('+', '00'),
    callInQueue: call.call_in_queue,
    callInQueueName: call.call_in_queue_name,
    fromSmsSupport: call.from_sms_support,
    toSmsSupport: call.to_sms_support,
    fromContactId: call.from_contact_id,
    toContactId: call.to_contact_id,
    toContactName: call.to_name_source === 'phonebook' ? call.to_name : null,
    fromContactName:
      call.from_name_source === 'phonebook' ? call.from_name : null,
    direction: call.direction,
    callId: call.call_id,
    from: call.from,
    fromLocal: call.from_local,
    fromName: call.from_name,
    fromExt: call.from_ext,
    fromExtName: call.from_ext_name,
    to: call.to,
    toName: call.to_name,
    toLocal: call.to_local,
    toExt: call.to_ext,
    toExtName: call.to_ext_name,
    status: call.status,
    duration: call.duration,
    callDate: call.call_date,
    callDirection: call.direction,
    recording: call.recording,
    soundPlay: true,
    answeredBy: call.answered_by,
    extra: call.extra,
    integrations: getIntegrations(call.extra),
    discriptiveTime: call.discriptive_time,
    lastApp: call.last_app,
    sipCallId: call?.sip_call_id,
    originalSipCallId: call?.original_sip_call_id,
    type: call?.type,
  }));
};

export const setLoading = action => ({
  type: actionTypes.CALL_LOGS_STORE,
  payload: {
    loading: action,
  },
});

export const setFetchErrorMessage = message => ({
  type: actionTypes.CALL_LOGS_FAILURE,
  payload: {
    message,
    loading: false,
  },
});

export const clearHistoryCustomFilters = () => ({
  type: actionTypes.CLEAR_HISTORY_CUSTOM_FILTERS,
});

export const setOpenEnhancedSearch = open => ({
  type: actionTypes.CALL_LOGS_OPEN_ENHANCED_SEARCH,
  payload: {
    open,
  },
});

export const performResetCallItems = reset => ({
  type: actionTypes.RESET_CALL_ITEMS,
  payload: reset,
});

// export const resetHistoryFilterOptions = () => ({
//   type: actionTypes.RESET_HISTORY_FILTER_OPTIONS,
// });

export const setNoMoreItems = noMoreItems => ({
  type: actionTypes.CALL_NO_MORE_ITEMS,
  payload: { noMoreItems },
});

export const setCallDetails = call => (dispatch, getState) => {
  const { callDetails } = getState().call;
  const { extensions } = getState().phone;
  const { isIntegrated } = getState().integrations;

  const index = callDetails.findIndex(e => e.id === call.id);

  if (index === -1) {
    const detailsFormmated = isIntegrated ? getDetailsFromTo(call) : call;

    const toAnsweredBy = extensions?.find(
      ext => ext.number === call.answeredBy && typeof ext.name !== 'undefined'
    );

    const newCallDetails = {
      ...detailsFormmated,
      toAnsweredBy: toAnsweredBy ? toAnsweredBy.name : null,
      numberTypeFrom: validateNumberType(
        call.fromLocal || call.from,
        call.userCountry
      ),
      numberType: validateNumberType(call.toLocal || call.to, call.userCountry),
    };

    dispatch({
      type: actionTypes.CALL_LOGS_CALL_DETAILS,
      payload: { callDetails: [...callDetails, newCallDetails] },
    });
  }
};

export const removeCallDetails = call => (dispatch, getState) => {
  const { callDetails } = getState().call;
  const newDetailsStack = callDetails.filter(e => e.id !== call.id);

  dispatch({
    type: actionTypes.CALL_LOGS_CALL_DETAILS,
    payload: { callDetails: newDetailsStack },
  });
};

export const resetCallDetails = () => (dispatch, getState) => {
  dispatch({
    type: actionTypes.CALL_LOGS_CALL_DETAILS,
    payload: { callDetails: [] },
  });
};

export const subtractOneWeek = start => ({
  type: actionTypes.SET_HISTORY_FILTER_OPTIONS,
  option: filterOptions.dateRange,
  payload: {
    start: dayjs(start)
      .subtract(1, 'day')
      .startOf('day')
      .format('YYYY-MM-DD HH:mm:ss'),
    end: dayjs(start)
      .startOf('day')
      .format('YYYY-MM-DD HH:mm:ss'),
  },
});

export const setPageOffset = page => ({
  type: actionTypes.SET_CALL_LOGS_PAGE_OFFSET,
  payload: {
    pageOffset: page,
  },
});

/**
 * Compouse the call history url with params
 * @returns a calllog url like /new/v1/call-log?...
 */
function createCalllogsParamsURL(selectedOptions) {
  const {
    dateRange,
    extensions: selectedExtensions,
    directions,
    queues,
    tags,
    types: selectedTypes,
    itemsPerPage,
    userExtension,
    searchFor,
    pageOffset,
  } = selectedOptions;

  let extensions;

  if (selectedTypes?.includes('All Calls')) {
    if (selectedExtensions.length) {
      extensions = selectedExtensions;
    } else {
      extensions = [];
    }
  } else {
    extensions = [userExtension, ...selectedExtensions];
  }

  const params = [
    `per_page=${itemsPerPage}`,
    `page=${pageOffset}`,
    ...(extensions?.length ? [`extensions=${extensions.join(',')}`] : []),
    ...(searchFor ? [`search_for=${searchFor}`] : []),
    ...(directions?.length ? [`type=${directions.join(',')}`] : []),
    ...(dateRange?.start ? [`from_date=${dateRange.start}`] : []),
    ...(dateRange?.end ? [`to_date=${dateRange.end}`] : []),
    ...(queues?.length ? [`queues=${queues.join(',')}`] : []),
    ...(tags?.length ? [`tags=${tags.join(',')}`] : []),
  ];

  return `${params.join('&')}&sort_by=call_date&sort_dir=desc`;
}

/**
 * Search for calls logs on the API
 * If <extension> is passed, it should returns "My Calls" only.
 * @requestCount - Used to control the maximum recursive requests.
 * @returns returns a call logs object or {}
 */
export const fetchCalllogs = (extension = null, requestCount = 1) => async (
  dispatch,
  getState
) => {
  try {
    const {
      resetCallItems,
      dateRange,
      hasDateRange,
      searchFor,
      pageOffset,
    } = getState().historyFilterOptions;

    const { phoneCallLogsAll, phoneCallLogsMy } = getState().call;
    const userTz = getState().auth.userTimeZone;

    const { start, end } = dateRange;
    const startDate = dayjs(start)
      .tz(userTz)
      .format('YYYY-MM-DD HH:mm:ss');
    const endDate = dayjs(end)
      .tz(userTz)
      .format('YYYY-MM-DD HH:mm:ss');

    const itemsPerPage = 100;

    const pageState = extension ? 'phoneCallLogsMy' : 'phoneCallLogsAll';
    const maxRecursiveRequest = 30;

    // eslint-disable-next-line no-nested-ternary
    const calllogsState = resetCallItems
      ? []
      : extension
      ? phoneCallLogsMy
      : phoneCallLogsAll;

    dispatch(setLoading(true));
    dispatch(setNoMoreItems(false));

    const calllogsQueryUrl = createCalllogsParamsURL({
      ...getState().historyFilterOptions,
      dateRange: {
        start: startDate,
        end: endDate,
      },
      itemsPerPage,
      userExtension: extension ?? '',
      searchFor,
    });

    const response = await api.calls.get.logsWithFilterOptions(
      calllogsQueryUrl
    );

    const rawCalllogs = response?.data?._embedded ?? [];

    // The API calllogs response data converted to camelcase
    let normalizedCalllogs = convertSnakeLogsToCamelCase(rawCalllogs);

    const { page_count, page_size, total_items } = response.data;

    const hasMorePages = total_items / (page_count * page_size) >= 1;

    dispatch(setPageOffset(hasMorePages ? pageOffset + 1 : 1));

    /* NOTE: This is a recursive operation:
     * When there is no logs for the given period then the logic will call fetchCalllogs again and again until find the _embedded !== []
     */
    if (
      !hasMorePages &&
      !response.data._embedded.length &&
      requestCount < maxRecursiveRequest &&
      !hasDateRange
    ) {
      dispatch(subtractOneWeek(startDate));
      dispatch(fetchCalllogs(extension, requestCount + 1));
      return;
    }

    if (requestCount >= maxRecursiveRequest) {
      dispatch(setNoMoreItems(true));
    }

    if (resetCallItems) {
      dispatch(performResetCallItems(false));
    }

    // Convert the callDate to the user's timezone
    normalizedCalllogs = normalizedCalllogs.map(item => ({
      ...item,
      callDate: dayjs(parseDate(item.callDate)).tz(
        userTz || DEFAULT_USER_TIMEZONE
      ),
    }));

    const calllogs = _.uniqBy(
      [...calllogsState.flatMap(item => item.data), ...normalizedCalllogs],
      'callId'
    );

    /*
     * Group calllogs by date e.g: ddd DD MMMM (NOT TIME)
     * const groupedLogs = { 25 July 2024: Array(31), ...}
     * */
    const groupedCalllogs = _.chain(calllogs)
      .groupBy(formatGroupName)
      .toPairs()
      .map(([groupName, items]) => ({
        groupName,
        data: _.orderBy(
          items,
          [item => dayjs(item.callDate).valueOf()],
          ['desc']
        ),
      }))
      .orderBy(
        [({ groupName }) => dayjs(groupName, 'DD MMMM YYYY').valueOf()],
        ['desc']
      )
      .value();

    dispatch({
      type: actionTypes.CALL_LOGS_SUCCESS,
      payload: {
        [pageState]: groupedCalllogs,
        loading: false,
      },
    });
  } catch (err) {
    log.error('Failed to fetch call logs.', err);
    dispatch(setFetchErrorMessage(err.message));
  }
};

export const updateCallRecordingStatus = callId => async (
  dispatch,
  getState
) => {
  try {
    const { phoneCallLogsMy, phoneCallLogsAll, callDetails } = getState().call;

    const myCalls = phoneCallLogsMy.map(callsByDate => ({
      ...callsByDate,
      data: callsByDate.data.map(lg =>
        lg.callId === callId ? { ...lg, recording: false } : lg
      ),
    }));

    const allCalls = phoneCallLogsAll.map(callsByDate => ({
      ...callsByDate,
      data: callsByDate.data.map(lg =>
        lg.callId === callId ? { ...lg, recording: false } : lg
      ),
    }));

    dispatch({
      type: actionTypes.CALL_LOGS_UPDATE_RECORDING_STATUS,
      payload: {
        phoneCallLogsMy: myCalls,
        phoneCallLogsAll: allCalls,
        callDetails: callDetails?.length
          ? [{ ...callDetails[0], recording: false }]
          : callDetails,
      },
    });
  } catch (error) {
    log.error('Failed to update call recording status.', error);
  }
};

export const resetCalls = () => dispatch => {
  dispatch({
    type: actionTypes.CALL_LOGS_RESET,
  });
};
